/**
 * Lo que impide inscribir una propuesta.
 *
 * Estas reglas vivian en linea dentro de la pagina de enlaces, mezcladas con
 * la vista y comparando fechas como texto. Aqui son una funcion pura, con los
 * mismos textos de siempre, para que el recuento previo del wizard y el boton
 * de inscribir no puedan discrepar: lo que el recuento muestra como
 * impedimento es exactamente lo que va a bloquear la inscripcion.
 *
 * Son impedimentos, no avisos. Lo que se puede subsanar no entra aqui: eso lo
 * advierte el panel de alertas y no bloquea nada.
 */

import { fechaCierre } from './horaCierre';

export type Impedimento = {
  motivo: string;
  /** El paso del wizard donde se resuelve, cuando hay uno */
  paso?: string;
};

/** Estados de la convocatoria en los que no se admiten inscripciones. */
export const ESTADOS_NO_INSCRIBIBLES = ['En espera', 'En proceso', 'Cerrada', 'No publicada'];

const CONVOCATORIA_NO_ABIERTA = 'No puedes inscribirte a una convocatoria que no esté abierta';

/** Al minuto, como comparaba la version anterior al armar las fechas como texto. */
const alMinuto = (fecha: Date): number => Math.floor(fecha.getTime() / 60000);

/**
 * Un documento tecnico esta aportado si tiene archivo. "Escrito y Video" se
 * diligencia en linea, asi que cuenta con tener el escrito o el enlace.
 */
export const tecnicoAportado = (doc: any): boolean =>
  doc?.tipo_documento === 'Escrito y Video'
    ? Boolean(doc?.escrito || doc?.url_video)
    : Boolean(doc?.url_participante);

/**
 * ¿Ya cerró la convocatoria?
 *
 * Solo dice que sí cuando consta: sin fechas, o sin una fecha de cierre que
 * se pueda leer, devuelve false. Para inscribir eso igual bloquea —lo hace
 * `impedimentosParaInscribir`—, pero no es razón para dar por cerrada una
 * convocatoria y cerrarle la pantalla a alguien.
 *
 * Esto es para la pantalla, no es un control de seguridad: el reloj que
 * decide es el del servidor, que lo comprueba al guardar. Aquí solo se evita
 * que alguien pierda el tiempo llenando algo que ya no se puede enviar.
 */
export const convocatoriaCerrada = (convocatoria: any, ahora: Date = new Date()): boolean => {
  const fechas: any[] = Array.isArray(convocatoria?.fechas) ? convocatoria.fechas : [];
  const cierre = fechas.find((f: any) => f?.clave === 'Cierre');

  if (!cierre?.valormax) return false;

  return alMinuto(fechaCierre(cierre.valormax)) <= alMinuto(ahora);
};

export const impedimentosParaInscribir = (
  convocatoria: any,
  postulacion: any,
  ahora: Date = new Date()
): Impedimento[] => {
  const impedimentos: Impedimento[] = [];

  const fechas: any[] = Array.isArray(convocatoria?.fechas) ? convocatoria.fechas : [];

  if (fechas.length === 0) {
    impedimentos.push({ motivo: CONVOCATORIA_NO_ABIERTA });
  } else {
    const cierre = fechas.find((f: any) => f?.clave === 'Cierre');
    if (!cierre?.valormax) {
      impedimentos.push({ motivo: 'No se encontró la fecha de cierre de la convocatoria' });
    } else if (alMinuto(fechaCierre(cierre.valormax)) <= alMinuto(ahora)) {
      impedimentos.push({ motivo: CONVOCATORIA_NO_ABIERTA });
    }
  }

  if (!String(postulacion?.nombre_propuesta ?? '').trim()) {
    impedimentos.push({
      motivo: 'Por favor digite el nombre de la propuesta',
      paso: 'cierre',
    });
  }

  const categoria = postulacion?.categoria_linea_convocatoria;
  if (categoria === null || categoria === undefined || categoria === '') {
    impedimentos.push({
      motivo: 'Debes seleccionar una categoría para tu propuesta para finalizar la inscripción.',
      paso: 'propuesta',
    });
  }

  /*
    La aceptacion se declara al inscribir, al final del ultimo paso. No se
    guarda: no vuelve del servidor y hay que marcarla cada vez que se
    inscribe, que es cuando se declara.
  */
  if (!postulacion?.politicas) {
    impedimentos.push({
      motivo:
        'Debes aceptar las Políticas de uso y los Términos y condiciones para finalizar la inscripción.',
      paso: 'cierre',
    });
  }

  const tecnicos: any[] = Array.isArray(postulacion?.documentos_tecnicos)
    ? postulacion.documentos_tecnicos
    : [];

  if (tecnicos.length > 0 && tecnicos.some((doc) => !tecnicoAportado(doc))) {
    impedimentos.push({
      motivo:
        'Debes cargar todos los documentos técnicos obligatorios para finalizar la inscripción.',
      paso: 'propuesta',
    });
  }

  return impedimentos;
};

/** El texto para el estado de la convocatoria, que se consulta aparte por red. */
export const impedimentoPorEstado = (estado: any): Impedimento | null =>
  ESTADOS_NO_INSCRIBIBLES.includes(String(estado))
    ? { motivo: CONVOCATORIA_NO_ABIERTA }
    : null;
